import { RacemapColors } from '@racemap/utilities/consts/common';
import { shuffleArray } from '@racemap/utilities/functions/utils';
import type { PointRust } from '@racemap/utilities/types/trackping';
import { ReadVariants, TypeOfSource } from '@racemap/utilities/types/types';
import tinyColor from 'tinycolor2';
import {
  IconBackward,
  IconCircleBan,
  IconCircleCheck,
  IconCircleQuestion,
  IconClock,
  IconFail,
  IconFinish,
  IconLocation,
  IconPlay,
  IconRacemap,
} from '../../BasicComponents/Icons';

export const SHADOW_TRACK_STEP = 10;

const colorList = shuffleArray([
  ...tinyColor('#af0').monochromatic(),
  ...tinyColor('#f73').monochromatic(),
  ...tinyColor('#3af').monochromatic(),
  ...tinyColor('#f024ff').monochromatic(),
]);

export const colorTable = {
  // Reads
  // Using a strong Green for accepted to signal success/validity clearly
  // acceptedReadColor: '#2e7d32',
  acceptedReadColor: RacemapColors.DarkBlue,

  // Using a strong Red for rejected to signal error/invalidity
  rejectedReadColor: '#c62828',
  // Using a neutral Gray for unused to de-emphasize them (reduced visual noise)
  unusedReadColor: '#9e9e9e',

  // Live (Active)
  // Using Orange for Live as it implies activity/energy and stands out
  liveColor: '#ff9800',
  liveSpeedColor: '#e65100', // Darker shade of Orange for correlated data
  // Using Indigo for "Ideal" - highly contrasting to Orange, represents "theoretical"
  liveIdeallyColor: '#7839ab',

  // Replay (Historical/Comparison)
  // Using Teal/Cyan for Replay - distinct from Live (Orange) and Reads (Green/Red)
  replayColor: RacemapColors.PaleBlue,
  replaySpeedColor: '#006064', // Darker shade of Teal

  // Estimation (Uncertainty)
  // Using Purple - distinct from the primary data sets
  estimationColor: '#9c27b0',
  estimationSpeedColor: '#4a148c', // Darker shade of Purple

  // Other Speeds
  measuredSpeedColor: '#78c964',
  averagedSpeedColor: '#57bc3e',
  defSpeedColor: '#541464',

  // Misc
  compensationPointColor: '#000000',
  // Using a muted Slate/Blue-Grey for Elevation so it acts as context/background
  elevationColor: '#607d8b',
};

export function calculateVelocityInKMH(a: PointRust, b: PointRust) {
  if (b.wasJump) {
    return 0;
  }
  return ((b.offset - a.offset) / (Date.parse(b.timestamp) - Date.parse(a.timestamp))) * 1000 * 3.6;
}

type MappingOfReadVariantIconProps = {
  variant: ReadVariants;
};

export const MappingOfReadVariantIcon = ({ variant }: MappingOfReadVariantIconProps) => {
  switch (variant) {
    case ReadVariants.Unclassified: {
      return (
        <IconCircleQuestion
          style={{ color: RacemapColors.PaleBlue }}
          title="This mapping of the related read could not be classified."
        />
      );
    }
    case ReadVariants.Rejected: {
      return (
        <IconFail
          style={{ color: RacemapColors.PaleBlue }}
          title="This mapping of the related read was rejected for some reason."
        />
      );
    }
    case ReadVariants.Regular: {
      return (
        <IconCircleCheck
          style={{ color: RacemapColors.PaleBlue }}
          title="This mapping of the related read is a regular one. Its referred to as accepted read. It passed all logical filters successfuly."
        />
      );
    }
    case ReadVariants.Unused: {
      return (
        <IconCircleBan
          style={{ color: RacemapColors.PaleBlue }}
          title="All mappings of the related read are unused. All mappings of the related read have been rejected."
        />
      );
    }
    case ReadVariants.HasBeenSelectedToBeTheStartRead: {
      return (
        <IconPlay
          style={{ color: RacemapColors.PaleBlue }}
          title="This Mapping of the related read has been identified to be the start of the prediction."
        />
      );
    }
    case ReadVariants.ThisReadClosesTheSearchForAStartRead: {
      return (
        <IconBackward
          style={{ color: RacemapColors.PaleBlue }}
          title="This mapping of the related read has been identified to close the search of a start read. Its a different reader a bunch of meters down the race track."
        />
      );
    }
    case ReadVariants.HasBeenSelectedToBeTheFinishRead: {
      return (
        <IconFinish
          style={{ color: RacemapColors.PaleBlue }}
          title="This mapping of the related read has been identified to be the a finish read. It's mapped within a range of 100m around the end of the shadow track."
        />
      );
    }
    case ReadVariants.HasBeenCalculatedToBeTheFinishRead: {
      return (
        <IconFinish
          style={{ color: RacemapColors.PaleBlue }}
          title="This mapping is synthetic. Cause we did not found a real finish read we predicted a reasonable finish time and positioned it at end of the shadow track."
        />
      );
    }
    default: {
      return (
        <>
          <IconFail
            style={{ color: RacemapColors.DangerRed }}
            title="We do not know the variant of this Mapping."
          />{' '}
          ({variant})
        </>
      );
    }
  }
};

type TypeOfSourceIconProps = {
  source?: string;
};

export const TypeOfSourceIcon = ({ source }: TypeOfSourceIconProps) => {
  if (source == null) return <IconFail title="We do not know the source of this Read." />;
  switch (source) {
    case TypeOfSource.RRForwarder: {
      return (
        <IconLocation
          style={{ color: RacemapColors.PaleBlue }}
          title="Forwarding: Reads are generated when passing RACE RESULT Track Boxes. The reads are forwarded to RACEMAP without being processed in the timing software. In most cases, forwarding generates multiple reads for each reader and transponder. Timestamp and location are determined with less accuracy."
        />
      );
    }
    case TypeOfSource.RRExporter: {
      return (
        <IconClock
          style={{ color: RacemapColors.PaleBlue }}
          title=" Exporter: Reads are generated through RACE RESULT timing software from detections with Track Boxes, Decoders, and Loop Boxes. Each read is associated with a timing point in RACE RESULT. Exporter generates not more than one read for each reader and transponder. Timestamp and location are determined with high accuracy."
        />
      );
    }
    case TypeOfSource.RacemapReadsAPI: {
      return (
        <IconRacemap
          style={{ color: RacemapColors.PaleBlue }}
          title="Timing Read: Reads sent from any timekeeping solution using RACEMAP timing API."
        />
      );
    }
    default: {
      return (
        <>
          <IconFail
            style={{ color: RacemapColors.DangerRed }}
            title="We do not know the source of this Read."
          />{' '}
          ({source})
        </>
      );
    }
  }
};

export const getAcceptedPingColor = (accPingId: number) => {
  return colorList[(accPingId || 0) % colorList.length];
};
